import { X, Loader2, Palette, ClipboardList, GitPullRequest, TestTube, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useImpactAnalysis } from '../../hooks/useDesignImpact';

interface Props {
  projectId: string;
  issueId: string;
  onClose: () => void;
}

const STATUS_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  DRAFT: { label: '초안', bg: 'bg-gray-100', text: 'text-gray-700' },
  IN_REVIEW: { label: '리뷰 중', bg: 'bg-blue-100', text: 'text-blue-700' },
  REVISION: { label: '수정 요청', bg: 'bg-amber-100', text: 'text-amber-700' },
  APPROVED: { label: '승인', bg: 'bg-green-100', text: 'text-green-700' },
  HANDED_OFF: { label: '핸드오프', bg: 'bg-purple-100', text: 'text-purple-700' },
  NEEDS_UPDATE: { label: '업데이트 필요', bg: 'bg-red-100', text: 'text-red-700' },
  OPEN: { label: 'Open', bg: 'bg-green-100', text: 'text-green-700' },
  MERGED: { label: 'Merged', bg: 'bg-purple-100', text: 'text-purple-700' },
  CLOSED: { label: 'Closed', bg: 'bg-red-100', text: 'text-red-700' },
};

export default function ImpactAnalysisPanel({ projectId, issueId, onClose }: Props) {
  const navigate = useNavigate();
  const { key: projectKey } = useParams<{ key: string }>();
  const { data, isLoading } = useImpactAnalysis(projectId, issueId, true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (section: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">영향도 분석</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-gray-400" /></div>}

          {data && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: <Palette size={14} />, label: '디자인', count: data.summary.totalDesignItems, warn: data.summary.designNeedsReReview },
                  { icon: <ClipboardList size={14} />, label: '태스크', count: data.summary.totalTasks },
                  { icon: <GitPullRequest size={14} />, label: 'PR', count: data.summary.totalPRs },
                  { icon: <TestTube size={14} />, label: '테스트', count: data.summary.totalTests },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {s.icon} {s.label} <span className="font-semibold">{s.count}건</span>
                    {s.warn && s.warn > 0 && (
                      <span className="text-amber-600 flex items-center gap-0.5"><AlertTriangle size={10} /> {s.warn}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Design Items */}
              {data.impacts.designItems.length > 0 && (
                <Section icon={<Palette size={14} />} title="디자인" count={data.impacts.designItems.length} collapsed={collapsed.has('design')} onToggle={() => toggle('design')}>
                  {data.impacts.designItems.map((d) => (
                    <TreeItem key={d.designItemId} onClick={() => navigate(`/projects/${projectKey}/design-items/${d.designItemId}`)}>
                      <span className="text-sm text-gray-900">{d.title}</span>
                      <Badge status={d.status} />
                      {d.needsReReview && <span className="text-[11px] text-amber-600 flex items-center gap-0.5"><AlertTriangle size={10} />재검토</span>}
                    </TreeItem>
                  ))}
                </Section>
              )}

              {/* Tasks */}
              {data.impacts.tasks.length > 0 && (
                <Section icon={<ClipboardList size={14} />} title="태스크" count={data.impacts.tasks.length} collapsed={collapsed.has('tasks')} onToggle={() => toggle('tasks')}>
                  {data.impacts.tasks.map((t) => (
                    <TreeItem key={t.issueId}>
                      <span className="text-sm font-mono text-gray-500">{t.issueKey}</span>
                      <span className="text-sm text-gray-900 truncate">{t.title}</span>
                      {t.hasOpenPR && <span className="text-[11px] text-green-600">PR Open</span>}
                    </TreeItem>
                  ))}
                </Section>
              )}

              {/* PRs */}
              {data.impacts.pullRequests.length > 0 && (
                <Section icon={<GitPullRequest size={14} />} title="Pull Requests" count={data.impacts.pullRequests.length} collapsed={collapsed.has('prs')} onToggle={() => toggle('prs')}>
                  {data.impacts.pullRequests.map((pr) => (
                    <TreeItem key={pr.prId}>
                      <span className="text-sm text-gray-500">#{pr.prNumber}</span>
                      <span className="text-sm text-gray-900 truncate">{pr.title}</span>
                      <Badge status={pr.state} />
                    </TreeItem>
                  ))}
                </Section>
              )}

              {/* Tests */}
              {data.impacts.testCases.length > 0 && (
                <Section icon={<TestTube size={14} />} title="테스트" count={data.impacts.testCases.length} collapsed={collapsed.has('tests')} onToggle={() => toggle('tests')}>
                  {data.impacts.testCases.map((tc) => (
                    <TreeItem key={tc.testCaseId}>
                      <span className="text-sm text-gray-900 truncate">{tc.title}</span>
                    </TreeItem>
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, count, collapsed, onToggle, children }: {
  icon: React.ReactNode; title: string; count: number; collapsed: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <button onClick={onToggle} className="flex items-center gap-2 w-full px-4 py-2.5 hover:bg-gray-50 text-left">
        {collapsed ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        <span className="text-gray-500">{icon}</span>
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <span className="text-sm text-gray-400">({count})</span>
      </button>
      {!collapsed && <div className="border-t border-gray-100 px-4 py-2 space-y-1">{children}</div>}
    </div>
  );
}

function TreeItem({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <div onClick={onClick} className={`flex items-center gap-2 px-3 py-1.5 rounded ${onClick ? 'hover:bg-gray-50 cursor-pointer' : ''}`}>
      {children}
    </div>
  );
}

function Badge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
}
